import React, { useState, useEffect } from "react";
import { Steps, message, Button, Modal, Spin } from "antd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { PostJobWrapper } from "./PostJob.style";
import SkillRequired from "./SkillRequired";
import BasicDetails from "./BasicDetails";
import Preferences from "./Preferences";
import JobResponsibilities from "./JobResponsibilities";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Step } = Steps;

const getDefaultFormData = (engagementId = null, projectId = null) => ({
  customerOrganization: "",
  role: "",
  experienceRange: "",
  primarySkills: [],
  secondarySkills: [],
  budget: null,
  budgetPeriod: "Per/month",
  reportingLocation: "",
  worldwide: false,
  monthsOfEngagement: "",
  engagementType: "",
  numberOfRequirements: null,
  numberOfRequirementsPeriod: "Per/month",
  tentativeStartDate: "",
  communication: "",
  workingTime: "",
  shiftTiming: "",
  shiftStart: null,
  shiftEnd: null,
  workingHoursPerWeek: null,
  travelPreference: "",
  provideSystem: "",
  toolsUsed: [],
  jobResponsibilities: "",
  engagementId: engagementId,
  projectId: projectId,
});

const PostJob = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const urlEngagementId = searchParams.get('engagementId');
  const urlProjectId = searchParams.get('projectId');
  const isEditMode = Boolean(jobId);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(getDefaultFormData(urlEngagementId, urlProjectId));
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(!isEditMode);

  useEffect(() => {
    const fetchJobData = async () => {
      if (!isEditMode) return;
      
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_CONST.GET_JOB_REQUIREMENT}/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.data.job) {
          const job = response.data.job;
          setFormData({
            ...getDefaultFormData(),
            customerOrganization: job.customerOrganization || "",
            role: job.role || "",
            experienceRange: job.experienceRange || "",
            primarySkills: job.primarySkills || [],
            secondarySkills: job.secondarySkills || [],
            budget: job.budget || null,
            budgetPeriod: job.budgetPeriod || "Per/month",
            reportingLocation: job.reportingLocation || "",
            worldwide: job.worldwide || false,
            monthsOfEngagement: job.monthsOfEngagement || "",
            engagementType: job.engagementType || "",
            numberOfRequirements: job.numberOfRequirements || null,
            numberOfRequirementsPeriod: job.numberOfRequirementsPeriod || "Per/month",
            tentativeStartDate: job.tentativeStartDate || "",
            communication: job.communication || "",
            workingTime: job.workingTime || "",
            shiftTiming: job.shiftTiming || "",
            shiftStart: job.shiftStart || null,
            shiftEnd: job.shiftEnd || null,
            workingHoursPerWeek: job.workingHoursPerWeek || null,
            travelPreference: job.travelPreference || "",
            provideSystem: job.provideSystem || "",
            toolsUsed: job.toolsUsed || [],
            jobResponsibilities: job.jobResponsibilities || "",
          });
          setDataLoaded(true);
        }
      } catch (error) {
        console.error("Error fetching job:", error);
        message.error("Failed to load job data");
        navigate("/customer/my-jobs");
      } finally {
        setLoading(false);
      }
    };
    
    fetchJobData();
  }, [isEditMode, jobId, navigate]);

  const steps = [
    {
      title: "Skill Required",
      component: SkillRequired,
    },
    {
      title: "Basic Details",
      component: BasicDetails,
    },
    {
      title: "Preferences",
      component: Preferences,
    },
    {
      title: "Job Responsibilities",
      component: JobResponsibilities,
    },
  ];

  const handleNext = (stepData) => {
    setFormData({ ...formData, ...stepData });
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (stepData) => {
    const rawData = { ...formData, ...stepData };
    
    const primarySkillsFiltered = (rawData.primarySkills || []).filter(s => s);
    
    const payload = {
      customerOrganization: rawData.customerOrganization,
      role: rawData.role,
      experienceRange: rawData.experienceRange,
      primarySkills: primarySkillsFiltered,
      secondarySkills: rawData.secondarySkills || [],
      budget: Number(rawData.budget) || 0,
      budgetPeriod: rawData.budgetPeriod || 'Per/month',
      reportingLocation: rawData.reportingLocation,
      worldwide: Boolean(rawData.worldwide),
      monthsOfEngagement: rawData.monthsOfEngagement,
      engagementType: rawData.engagementType,
      numberOfRequirements: Number(rawData.numberOfRequirements) || 1,
      numberOfRequirementsPeriod: rawData.numberOfRequirementsPeriod || 'Per/month',
      tentativeStartDate: rawData.tentativeStartDate || null,
      communication: rawData.communication,
      workingTime: rawData.workingTime,
      workingHoursPerWeek: Number(rawData.workingHoursPerWeek) || 40,
      travelPreference: rawData.travelPreference,
      provideSystem: rawData.provideSystem,
      toolsUsed: rawData.toolsUsed || [],
      shiftTiming: rawData.shiftTiming,
      shiftStart: rawData.shiftStart || null,
      shiftEnd: rawData.shiftEnd || null,
      jobResponsibilities: rawData.jobResponsibilities || '',
      status: 'open',
      engagementId: rawData.engagementId || urlEngagementId,
      projectId: rawData.projectId || urlProjectId || null,
    };
    
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      
      if (isEditMode) {
        await axios.put(`${API_CONST.UPDATE_JOB_REQUIREMENT}/${jobId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success("Job updated successfully!");
      } else {
        await axios.post(API_CONST.ADD_JOB_REQUIREMENTS, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success("Job submitted for approval! It will be visible to partners once approved by admin.");
      }
      
      navigate("/customer/my-jobs");
    } catch (error) {
      console.error("Error submitting job:", error);
      message.error(isEditMode ? "Failed to update job" : "Failed to post job");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    Modal.confirm({
      title: "Cancel Job Posting",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: (
        <div>
          <p>Are you sure you want to cancel?</p>
          <p style={{ color: "#666", fontSize: 13 }}>
            All unsaved changes will be lost.
          </p>
        </div>
      ),
      okText: "Yes, Cancel",
      okButtonProps: { 
        danger: true,
      },
      cancelText: "Continue Editing",
      onOk: () => {
        navigate("/customer/my-jobs");
      },
    });
  };

  const CurrentStepComponent = steps[currentStep].component;

  if (isEditMode && !dataLoaded) {
    return (
      <PostJobWrapper>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Spin size="large" tip="Loading job data..." />
        </div>
      </PostJobWrapper>
    );
  }

  return (
    <PostJobWrapper>
      <div className="post-job-header">
        <div className="header-content">
          <h2>{isEditMode ? "Edit Job" : "Post Job"}</h2>
          {isEditMode && (
            <span style={{ color: "#666", fontSize: 14, marginLeft: 12 }}>
              Job ID: {jobId}
            </span>
          )}
        </div>
      </div>

      <div className="steps-container">
        <Steps current={currentStep}>
          {steps.map((step, index) => (
            <Step key={index} title={step.title} />
          ))}
        </Steps>
      </div>

      <div className="form-card">
        <CurrentStepComponent
          key={dataLoaded ? 'loaded' : 'loading'}
          initialData={formData}
          onNext={handleNext}
          onBack={handleBack}
          onSubmit={handleSubmit}
          isFirstStep={currentStep === 0}
          isLastStep={currentStep === steps.length - 1}
          isEditMode={isEditMode}
        />
      </div>
    </PostJobWrapper>
  );
};

export default PostJob;
